import { useCallback, useEffect, useState } from "react";
import { Route, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { roleLabel } from "@/components/TeamMemberForm";

const ownerLine = (type) => {
  if (type.owner) return `${type.owner.name} · ${roleLabel(type.owner.role)}${type.owner.active ? "" : " · deactivated — falls back to role default"}`;
  if (type.pending_name) return `${type.pending_name} · not yet on the team — ${roleLabel(type.default_role)} default applies until their account exists`;
  return `Role default · first active ${roleLabel(type.default_role)}`;
};

export const TaskRoutingCard = ({ canEdit, onSaved }) => {
  const [view, setView] = useState(null);
  const [owners, setOwners] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => api.get("/tasks/routing").then((response) => setView(response.data)).catch((requestError) => setError(formatApiError(requestError))), []);
  useEffect(() => { load(); }, [load]);

  const startEdit = async () => {
    setError("");
    try {
      const response = await api.get("/tasks/owners");
      setOwners(response.data);
      setDraft(Object.fromEntries(view.types.map((type) => [type.key, type.owner?.id || ""])));
      setEditing(true);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const original = Object.fromEntries(view.types.map((type) => [type.key, type.owner?.id || ""]));
      const changed = Object.fromEntries(Object.entries(draft).filter(([key, value]) => value !== original[key]));
      const response = await api.put("/tasks/routing", { routes: changed });
      setView(response.data);
      setEditing(false);
      const moved = Object.entries(response.data.moved || {}).filter(([, count]) => count > 0);
      onSaved?.(moved.length ? `Task routing saved — ${moved.map(([key, count]) => `${count} ${response.data.types.find((t) => t.key === key)?.label.toLowerCase()} task${count === 1 ? "" : "s"}`).join(", ")} moved to the new owner${moved.length === 1 ? "" : "s"}.` : "Task routing saved.");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  if (!view && !error) return null;
  return (
    <section className="email-delivery-card task-routing-card" data-testid="task-routing-card">
      <div className="section-heading">
        <div>
          <h2><Route size={16} /> Task routing</h2>
          <p>Who owns each automatic task type. New tasks go to the named owner; saving a change also moves that type's open tasks.{view?.updated_at ? ` Last changed ${new Date(view.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} by ${view.updated_by_name}.` : ""}</p>
        </div>
        {canEdit && !editing && view && (
          <button className="button button-secondary" data-testid="task-routing-edit" type="button" onClick={startEdit}>Edit routing</button>
        )}
      </div>
      {error && <p className="form-message error-message" data-testid="task-routing-error">{error}</p>}
      {view && !editing && (
        <ul className="task-routing-list" data-testid="task-routing-list">
          {view.types.map((type) => (
            <li key={type.key} data-testid={`task-routing-${type.key}`}>
              <div>
                <strong>{type.label}</strong>
                <small>{type.description}</small>
              </div>
              <div className="task-routing-owner">
                <span className={type.owner?.active ? "" : "task-routing-fallback"} data-testid={`task-routing-owner-${type.key}`}>{ownerLine(type)}</span>
                <small data-testid={`task-routing-count-${type.key}`}>{type.open_count} open</small>
              </div>
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <form className="task-routing-form" data-testid="task-routing-form" onSubmit={save}>
          {view.types.map((type) => (
            <label key={type.key}>
              <span className="field-label">{type.label}</span>
              <select data-testid={`task-routing-select-${type.key}`} value={draft[type.key] || ""} onChange={(event) => setDraft((current) => ({ ...current, [type.key]: event.target.value }))}>
                <option value="">Role default · first active {roleLabel(type.default_role)}</option>
                {owners.map((owner) => <option key={owner.id} value={owner.id}>{owner.name} · {roleLabel(owner.role)}</option>)}
              </select>
              {type.pending_name && !draft[type.key] && <small className="task-routing-fallback">Waiting for {type.pending_name} to join the team</small>}
            </label>
          ))}
          <div className="team-confirm-actions">
            <button className="button button-secondary" data-testid="task-routing-cancel" type="button" onClick={() => setEditing(false)}>Cancel</button>
            <button className="button button-primary" data-testid="task-routing-save" disabled={busy} type="submit"><Save size={14} /> {busy ? "Saving…" : "Save routing"}</button>
          </div>
        </form>
      )}
    </section>
  );
};
